import React, { KeyboardEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import Slider from "react-slick";
import { EventListener } from "event-source-polyfill";

import BlurryLoadingImage from "../BlurryLoadingImage";
import SlickSliderControls from "../content-blocks/SlickSliderControls";

import { useImageBuilder } from "../../utils/image";
import { useNavigate } from "react-router-dom";
import { useLinkBuilder } from "../../utils/link";
import { useTranslation } from "../../context/translation";
import { ariaMiles, stripHtml } from "../../utils/static";

import $ from "jquery";
import "slick-carousel/slick/slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

export function setCarouselHeight() {
  var maxHeight = 0;
  $(".carousel .mam-slide").each(function(index) {
    $(this).height("");
    // @ts-ignore
    if ($(this).outerHeight() > maxHeight) maxHeight = $(this).outerHeight();
  });
  $(".carousel .mam-slide").height(maxHeight);
  maxHeight = maxHeight + 5;
  $(".carousel .slick-slide").height(maxHeight);
}

export function setCarouselAccessibility() {
  $(".slick-cloned").each(function() {
    $(this).find(".mam-slide").each(function() {
      $(this).attr("aria-hidden", "true");
      $(this).attr("tabindex", "-1");
    });
  });
}

type SlickSliderProps = {
  id?: string;
  items: Array<any>;
  settings?: any;
  pauseBtn?: boolean;
}

type SlideItemProps<T> = {
  className?: string;
  item?: T | any;
  style?: CSSStyleSheet;
  tabIndex?: number;
}

function SlideItem<T>(props: SlideItemProps<T>) {
  const { buildImageUrl } = useImageBuilder();
  const { buildLinkByPage } = useLinkBuilder();
  const { translate } = useTranslation();

  const {
    item,
    style
  } = props;

  const pauseDots = () => {
    $(".mam-dots li.slick-active").addClass("paused");
  };

  const runDots = () => {
    $(".mam-dots li.slick-active.paused").removeClass("paused");
  };

  const alternativeLogoText = (item?.logo?.content_name && item.link?.bind_value) ? `${item.logo.content_name} ${item.link?.bind_value}` : item?.link?.title ?? stripHtml(item?.headline);
  return (
    <a className="mam-slide" href={item.link ? buildLinkByPage(item.link) : undefined} tabIndex={0}
       onMouseEnter={pauseDots} onMouseLeave={runDots}
       aria-label={translate("mam.static.to_page_aria", (item?.logo?.content_name && item.link?.bind_value) ? `${item.link?.bind_value}` : item?.link?.title ?? stripHtml(item?.headline))}>
      <div className="mam-slide" tabIndex={-1} aria-hidden={true}>
        <div className="center-block">
          <BlurryLoadingImage
            preview={buildImageUrl(item.image, 180, 73)}
            image={buildImageUrl(item.image, 2560, 1024)}
            alt={item?.headline ? stripHtml(item.headline) : "Image"}
          />
          {item?.flag && (
            <aside role="note" className="flag" dangerouslySetInnerHTML={{ __html: item.flag }}
                   aria-label={translate("mam.static.flag.label")} />)}
        </div>
        <div className="card-img-overlay">
          <div className="col-xs-11 col-sm-11 offset-sm-1">
            <div className="row mx-0">
              <div className="col-xs-11 col-sm-11 col-lg-3 left">
                <img src={buildImageUrl(item.logo, 202, 128)} alt={alternativeLogoText} />
              </div>
              <div className="col-xs-11 col-sm-11 col-lg-9 right">
                <div className="headline" dangerouslySetInnerHTML={{ __html: ariaMiles(item?.headline, translate) }} />
                <div className="benefit" dangerouslySetInnerHTML={{ __html: ariaMiles(item?.text, translate) }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </a>
  );
}

const defaultSlickSliderSettings = {
  slidesToShow: 1,
  slidesToScroll: 1,
  centerMode: true,
  autoplay: true,
  autoplaySpeed: 3000,
  dots: true,
  arrows: false,
  accessibility: true,
  focusOnSelect: true
};

export default function SlickSlider({
                                      id = "slick-slider",
                                      settings = defaultSlickSliderSettings,
                                      items,
                                      pauseBtn = false
                                    }: SlickSliderProps) {
  const sliderRef = useRef<Slider>();

  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  useLayoutEffect(() => {
    const interval = setInterval(() => {
      const slides = document.querySelectorAll(".slick-slide");
      slides.forEach(slide => {
        slide.removeAttribute("aria-hidden");
      });
    }, 500);
    return () => clearInterval(interval);
  });

  const resizeIt = () => {
    setTimeout(() => setCarouselAccessibility(), 150);
  };

  useLayoutEffect(() => {
    resizeIt();

    window.addEventListener("resize", resizeIt);
    return () => {
      window.removeEventListener("resize", resizeIt);
    };
  }, []);

  const getFilteredSlides = () => {
    const elements = document.getElementsByClassName('slick-slide');
    const filteredElements = [];
    for (const element of Array.from(elements)) {
      if (!element.classList.contains('slick-cloned')) {
        filteredElements.push(element);
      }
    }
    return filteredElements;
  };

  useEffect(() => {
    const focusChange = (element: Element) => {
      setTimeout(() => $(element).find('.mam-slide').focus(), 500);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Tab") {

        if (!event.shiftKey) {
          if (document.activeElement?.classList.contains('mam-slide')) {
            event.preventDefault();
            $('.pause-control').focus();
          }
        } else {
          if (document.activeElement?.classList.contains('pause-control')) {
            event.preventDefault();
            $('.mam-slide').first().focus();
          }
        }
      } else {
        const filteredElements = getFilteredSlides();
        if (event.key === "Enter" && (document.activeElement?.classList.contains('slick-slide') || document.activeElement?.classList.contains('mam-slide'))) {
          const url = $(filteredElements[currentSlide]).find('.mam-slide').attr('href');
          if (url) {
            navigate(url);
          }
        }
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          focusChange(filteredElements[currentSlide]);
        }
      }
    };

    document.addEventListener("keydown", onKeyDown as EventListener);
    return () => {
      document.removeEventListener("keydown", onKeyDown as EventListener);
    }
  }, [currentSlide]);

  const renderSlideItem = (item: any, index: number) => {
    if (!item) return null;
    return <SlideItem key={index} item={item} />;
  };

  if (items.length === 0) return null;

  return (
    <div className="mam-slide-container">
      <Slider
        className="carousel"
        role="region"
        ref={sliderRef}
        {...{
          ...settings,
          accessibility: true,
          arrows: false,
          dots: false,
          beforeChange: (current, next) => setCurrentSlide(next)
        }}
      >
        {items.map(renderSlideItem)}
      </Slider>
      <SlickSliderControls
        autoPlay
        sliderRef={sliderRef}
        showPause={pauseBtn}
        showDots={settings.dots ?? false}
        showArrows={settings.arrows ?? false}
        slideCount={items.length ?? 0}
        activeSlide={currentSlide}
      />
    </div>
  );
}
